import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getDashboard, getProjets, migrateData, updateProjet, deleteProjet, reorderProjets, getEnseignes } from '../services/api';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  Building2,
  FolderOpen,
  AlertTriangle,
  Plus,
  RefreshCw,
  Clock,
  Pause,
  Play,
  FileText,
  CheckCircle,
  Archive,
  Trash2,
  ChevronDown,
  ChevronRight,
  Calendar,
  GripVertical,
  FileSignature,
  Target
} from 'lucide-react';
import { toast } from 'sonner';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable table row component
const SortableRow = ({ projet, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: projet.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    zIndex: isDragging ? 10 : 'auto',
  };
  return (
    <tr ref={setNodeRef} style={style} className="hover:bg-slate-50/50" data-testid={`projet-row-${projet.id}`}>
      <td className="px-1 py-4 w-8">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-slate-300 hover:text-slate-500" data-testid="drag-handle">
          <GripVertical className="w-4 h-4" />
        </button>
      </td>
      {children}
    </tr>
  );
};

// Static (non-draggable) table row used in collapsible sections
const StaticRow = ({ projet, children }) => (
  <tr className="hover:bg-slate-50/50" data-testid={`projet-row-${projet.id}`}>
    <td className="px-1 py-4 w-8"></td>
    {children}
  </tr>
);

const DashboardPage = () => {
  const [dashboard, setDashboard] = useState(null);
  const [projets, setProjets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [activeTab, setActiveTab] = useState('en_cours'); // 'en_cours' or 'archives'
  const [expandedYears, setExpandedYears] = useState({});
  const [showStandBy, setShowStandBy] = useState(false);
  const [showTermines, setShowTermines] = useState(false);
  const [kpi, setKpi] = useState(null);
  const [moisManquantsAlerte, setMoisManquantsAlerte] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [dashRes, projetsRes] = await Promise.all([
        getDashboard(),
        getProjets()
      ]);
      setDashboard(dashRes.data);
      setProjets(projetsRes.data);
      setLoading(false);
      // Non-blocking admin widgets (KPI + relevés manquants) en parallèle, après le premier rendu
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/suivi-bancaire/kpi`, { headers })
        .then((kpiRes) => setKpi(kpiRes.data))
        .catch(() => {});
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/suivi-bancaire/alerte-mois-manquants`, { headers })
        .then((alerteRes) => setMoisManquantsAlerte(alerteRes.data.mois_manquants || []))
        .catch(() => {});
    } catch (e) {
      console.error('Error loading dashboard:', e);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Séparer projets actifs et archivés, triés par statut puis par display_order
  const statutOrder = { 'en_cours': 1, 'en_preparation': 2, 'preparation': 2, 'termine_facturation': 3, 'stand_by': 4 };
  const sortedActifs = projets
    .filter(p => p.statut !== 'cloture')
    .sort((a, b) => {
      // Si un ordre personnalisé existe pour les deux, l'utiliser
      if (a.display_order != null && b.display_order != null) {
        return a.display_order - b.display_order;
      }
      // Sinon tri par statut
      return (statutOrder[a.statut] || 99) - (statutOrder[b.statut] || 99);
    });
  const [projetsActifs, setProjetsActifs] = useState([]);
  const [showMargeDetail, setShowMargeDetail] = useState(false);
  const [showSignesDetail, setShowSignesDetail] = useState(false);
  const [groupByEnseigne, setGroupByEnseigne] = useState(() => localStorage.getItem('dash_group_enseigne') === '1');
  const navigate = useNavigate();
  const [newProjetOpen, setNewProjetOpen] = useState(false);
  const [enseignesList, setEnseignesList] = useState([]);
  const [newProjetEnseigne, setNewProjetEnseigne] = useState('');
  const openNewProjet = async () => {
    setNewProjetOpen(true);
    if (enseignesList.length === 0) {
      try {
        const res = await getEnseignes();
        const list = [...(res.data || [])].sort((a, b) => (a.nom || '').localeCompare(b.nom || ''));
        setEnseignesList(list);
      } catch (e) {
        toast.error('Impossible de charger les enseignes');
      }
    }
  };
  const confirmNewProjet = () => {
    if (!newProjetEnseigne) return toast.error('Choisissez une enseigne');
    setNewProjetOpen(false);
    navigate(`/projets/nouveau?enseigne=${newProjetEnseigne}`);
  };
  const toggleGroupByEnseigne = () => {
    setGroupByEnseigne((v) => { localStorage.setItem('dash_group_enseigne', v ? '0' : '1'); return !v; });
  };
  const formatDateCourt = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-');
  // Regroupe une liste de projets par enseigne (tri alphabétique, puis dossiers les plus récents en premier)
  const groupByEns = (list) => {
    const map = new Map();
    list.forEach((p) => {
      const k = p.enseigne_nom || 'Sans enseigne';
      if (!map.has(k)) map.set(k, { enseigne_nom: k, enseigne_id: p.enseigne_id, projets: [], ca: 0 });
      const g = map.get(k); g.projets.push(p); g.ca += p.ca_maag || 0;
    });
    return Array.from(map.values())
      .sort((a, b) => a.enseigne_nom.localeCompare(b.enseigne_nom, 'fr'))
      .map((g) => ({ ...g, projets: [...g.projets].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')) }));
  };
  
  useEffect(() => {
    setProjetsActifs(sortedActifs);
  }, [projets]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const projetsArchives = projets.filter(p => p.statut === 'cloture');

  // 3 sous-groupes affichés dans l'onglet "En cours" :
  //  - Prioritaires (toujours visibles, drag-and-drop actif) : en_cours + en_preparation
  //  - Stand-by (collapse, fermé par défaut)
  //  - Terminé facturation (collapse, fermé par défaut)
  const projetsPrioritaires = projetsActifs.filter(p => p.statut === 'en_cours' || p.statut === 'en_preparation' || p.statut === 'preparation');
  const projetsStandBy = projetsActifs.filter(p => p.statut === 'stand_by');
  const projetsTermines = projetsActifs.filter(p => p.statut === 'termine_facturation');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    const oldIndex = projetsActifs.findIndex(p => p.id === active.id);
    const newIndex = projetsActifs.findIndex(p => p.id === over.id);
    
    const reordered = arrayMove(projetsActifs, oldIndex, newIndex);
    setProjetsActifs(reordered);
    
    // Save order to backend
    try {
      const order = reordered.map((p, idx) => ({ id: p.id, display_order: idx }));
      await reorderProjets(order);
    } catch (e) {
      console.error('Error saving order:', e);
    }
  }, [projetsActifs]);

  // Grouper les archives par année puis par enseigne
  const archivesParAnnee = projetsArchives.reduce((acc, projet) => {
    // Déterminer l'année (date_fin ou created_at)
    const dateStr = projet.date_fin || projet.created_at;
    const year = dateStr ? new Date(dateStr).getFullYear() : new Date().getFullYear();
    
    if (!acc[year]) {
      acc[year] = {};
    }
    
    const enseigne = projet.enseigne_nom || 'Sans enseigne';
    if (!acc[year][enseigne]) {
      acc[year][enseigne] = [];
    }
    acc[year][enseigne].push(projet);
    
    return acc;
  }, {});

  // Trier les années en ordre décroissant
  const anneesTriees = Object.keys(archivesParAnnee).sort((a, b) => b - a);

  const toggleYear = (year) => {
    setExpandedYears(prev => ({
      ...prev,
      [year]: !prev[year]
    }));
  };

  const handleUpdateDate = async (projetId, field, value) => {
    try {
      await updateProjet(projetId, { [field]: value });
      toast.success('Date mise à jour');
      loadData();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleUpdateStatut = async (projetId, newStatut) => {
    try {
      await updateProjet(projetId, { statut: newStatut });
      toast.success('Statut mis à jour');
      loadData();
    } catch (e) {
      toast.error('Erreur mise à jour statut');
    }
  };

  const handleDeleteProjet = async (projetId, projetNom) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le projet "${projetNom}" ?\n\nCette action supprimera définitivement :\n- Tous les devis\n- Toutes les factures\n- Tous les achats artisans\n- Tous les plannings\n\nCette action est irréversible !`)) return;
    
    try {
      await deleteProjet(projetId);
      toast.success('Projet supprimé');
      loadData();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleMigrate = async () => {
    if (!window.confirm('Migrer les données existantes vers la nouvelle structure ?')) return;
    setMigrating(true);
    try {
      const res = await migrateData();
      toast.success(`Migration terminée: ${res.data.migrated.enseignes} enseignes, ${res.data.migrated.projets} projets, ${res.data.migrated.devis} devis`);
      loadData();
    } catch (e) {
      toast.error('Erreur lors de la migration');
    } finally {
      setMigrating(false);
    }
  };

  const formatMoney = (value) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const getMargeColor = (pct) => {
    if (pct === null || pct === undefined) return 'text-slate-400';
    if (pct < 15) return 'text-red-600';
    if (pct < 20) return 'text-orange-500';
    return 'text-green-600';
  };

  const STATUT_OPTIONS = [
    { value: 'en_preparation', label: 'Préparation', className: 'bg-blue-100 text-blue-800 border-blue-200' },
    { value: 'en_cours', label: 'En cours', className: 'bg-green-100 text-green-800 border-green-200' },
    { value: 'termine_facturation', label: 'Terminé – facturation', className: 'bg-purple-100 text-purple-800 border-purple-200' },
    { value: 'stand_by', label: 'Stand-by', className: 'bg-amber-100 text-amber-800 border-amber-200' },
    { value: 'cloture', label: 'Clôturé', className: 'bg-slate-700 text-white border-slate-800' },
    { value: 'projet_non_realise', label: 'Non réalisé', className: 'bg-slate-200 text-slate-700 border-slate-300' },
  ];

  const renderStatutSelect = (projet) => {
    const opt = STATUT_OPTIONS.find(o => o.value === projet.statut) || STATUT_OPTIONS[0];
    return (
      <select
        value={projet.statut}
        onChange={(e) => handleUpdateStatut(projet.id, e.target.value)}
        onClick={(e) => e.stopPropagation()}
        className={`text-xs font-medium px-2 py-1 rounded border cursor-pointer focus:outline-none focus:ring-2 focus:ring-slate-900 ${opt.className}`}
        data-testid={`statut-select-${projet.id}`}
      >
        {STATUT_OPTIONS.map(o => (
          <option key={o.value} value={o.value} className="bg-white text-slate-900">{o.label}</option>
        ))}
      </select>
    );
  };

  const getStatutBadge = (statut) => {
    switch (statut) {
      case 'en_cours':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"><Play className="w-3 h-3 mr-1" />En cours</span>;
      case 'en_preparation':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"><Clock className="w-3 h-3 mr-1" />Préparation</span>;
      case 'termine_facturation':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800"><FileText className="w-3 h-3 mr-1" />Terminé – facturation</span>;
      case 'stand_by':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800"><Pause className="w-3 h-3 mr-1" />Stand-by</span>;
      case 'cloture':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-700 text-white"><CheckCircle className="w-3 h-3 mr-1" />Clôturé</span>;
      default:
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-800">{statut}</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Cellules communes à toutes les lignes projet (partagées entre SortableRow et StaticRow)
  const renderRowCells = (projet) => {
    let dureeLabel = '-';
    if (projet.date_debut && projet.date_fin) {
      const debut = new Date(projet.date_debut);
      const fin = new Date(projet.date_fin);
      const diffMs = fin - debut;
      const diffWeeks = Math.ceil(diffMs / (1000 * 60 * 60 * 24 * 7));
      if (diffWeeks > 0) dureeLabel = `${diffWeeks} sem.`;
    }
    return (
      <>
        <td className="px-4 py-4">
          <Link to={`/projets/${projet.id}`} className="font-medium text-slate-900 hover:text-blue-600">
            {groupByEnseigne ? (projet.ville || projet.nom_complet) : projet.nom_complet}
          </Link>
        </td>
        <td className="px-3 py-4 text-sm text-slate-500 whitespace-nowrap font-mono" data-testid={`created-at-${projet.id}`}>{formatDateCourt(projet.created_at)}</td>
        <td className="px-4 py-4">{renderStatutSelect(projet)}</td>
        <td className="px-3 py-2">
          <input
            type="date"
            value={projet.date_debut || ''}
            onChange={(e) => handleUpdateDate(projet.id, 'date_debut', e.target.value)}
            className="h-8 px-2 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900 bg-white"
          />
        </td>
        <td className="px-3 py-2">
          <input
            type="date"
            value={projet.date_fin || ''}
            onChange={(e) => handleUpdateDate(projet.id, 'date_fin', e.target.value)}
            className="h-8 px-2 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900 bg-white"
          />
        </td>
        <td className="px-4 py-4 text-center">
          <span className={`text-xs font-medium px-2 py-1 rounded ${dureeLabel !== '-' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
            {dureeLabel}
          </span>
        </td>
        <td className="px-4 py-4 text-right font-mono text-sm">{formatMoney(projet.ca_maag)}</td>
        <td className="px-4 py-4 text-right font-mono text-sm text-slate-500">{formatMoney(projet.total_achats_artisans)}</td>
        <td className={`px-4 py-4 text-right font-mono text-sm font-semibold ${getMargeColor(projet.marge_pourcentage)}`}>
          {formatMoney(projet.marge_euros)}
        </td>
        <td className={`px-4 py-4 text-right font-mono text-sm font-bold ${getMargeColor(projet.marge_pourcentage)}`}>
          {projet.marge_pourcentage !== null ? `${projet.marge_pourcentage.toFixed(1)}%` : '-'}
        </td>
        <td className="px-2 py-4 text-center">
          <button
            onClick={() => handleDeleteProjet(projet.id, projet.nom_complet)}
            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
            title="Supprimer le projet"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </td>
      </>
    );
  };

  // Lignes groupées par enseigne (desktop) : en-tête de groupe + lignes statiques (pas de drag & drop en mode groupé)
  const renderGroupedRows = (list) => groupByEns(list).map((g) => (
    <React.Fragment key={g.enseigne_nom}>
      <tr className="bg-slate-100/80" data-testid={`ens-group-${g.enseigne_nom}`}>
        <td colSpan="12" className="px-4 py-2">
          <div className="flex items-center gap-3">
            <Building2 className="w-4 h-4 text-slate-500" />
            <Link to={g.enseigne_id ? `/clients?enseigne=${g.enseigne_id}` : '/clients'} className="text-xs font-bold uppercase tracking-wider text-slate-700 hover:text-blue-600">{g.enseigne_nom}</Link>
            <span className="text-[11px] text-slate-500 bg-white border border-slate-200 px-2 py-0.5 rounded-full">{g.projets.length} dossier{g.projets.length > 1 ? 's' : ''}</span>
            <span className="ml-auto text-xs font-mono text-slate-600">{formatMoney(g.ca)}</span>
          </div>
        </td>
      </tr>
      {g.projets.map((projet) => (
        <StaticRow key={projet.id} projet={projet}>{renderRowCells(projet)}</StaticRow>
      ))}
    </React.Fragment>
  ));

  const renderGroupedMobile = (list) => groupByEns(list).map((g) => (
    <div key={g.enseigne_nom}>
      <div className="px-4 py-2 bg-slate-100/80 flex items-center gap-2">
        <Building2 className="w-4 h-4 text-slate-500" />
        <span className="text-xs font-bold uppercase tracking-wider text-slate-700">{g.enseigne_nom}</span>
        <span className="text-[11px] text-slate-500">({g.projets.length})</span>
        <span className="ml-auto text-xs font-mono text-slate-600">{formatMoney(g.ca)}</span>
      </div>
      <div className="divide-y divide-slate-100">{g.projets.map(renderMobileCard)}</div>
    </div>
  ));

  // Table entête (partagée par les 3 sections)
  const renderTableHead = () => (
    <thead className="bg-slate-50">
      <tr>
        <th className="px-1 py-3 w-8"></th>
        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Projet</th>
        <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">Créé le</th>
        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Statut</th>
        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Début</th>
        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Fin</th>
        <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-500">Durée</th>
        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">CA MAAG</th>
        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Coûts</th>
        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Marge</th>
        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">%</th>
        <th className="px-2 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-500"></th>
      </tr>
    </thead>
  );

  // Carte mobile projet (partagée par les 3 sections)
  const renderMobileCard = (projet) => (
    <Link
      key={projet.id}
      to={`/projets/${projet.id}`}
      className="block p-4 hover:bg-slate-50 transition-colors"
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-900 text-sm truncate">{groupByEnseigne ? (projet.ville || projet.nom_complet) : projet.nom_complet}</p>
          <p className="text-[11px] text-slate-400 mt-0.5">Créé le {formatDateCourt(projet.created_at)}</p>
          <div className="mt-1" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>{renderStatutSelect(projet)}</div>
        </div>
        <div className="text-right ml-3">
          <p className="font-bold text-slate-900 text-sm">{formatMoney(projet.ca_maag)}</p>
          <p className={`text-xs font-semibold ${getMargeColor(projet.marge_pourcentage)}`}>
            {projet.marge_pourcentage !== null ? `${projet.marge_pourcentage.toFixed(1)}%` : '-'}
          </p>
        </div>
      </div>
    </Link>
  );

  return (
    <div data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-6 lg:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Tableau de bord</h1>
          <p className="text-slate-500 mt-1 text-sm lg:text-base">Pilotage global MAAG</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleMigrate}
            disabled={migrating}
            className="flex items-center px-3 lg:px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${migrating ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{migrating ? 'Migration...' : 'Migrer données'}</span>
            <span className="sm:hidden">{migrating ? '...' : 'Migrer'}</span>
          </button>
        </div>
      </div>

      {/* KPIs principaux */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-6 mb-6 lg:mb-8">
        {/* CA MAAG + Marge */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 lg:p-6 shadow-sm" data-testid="kpi-ca-maag-card">
          <div className="flex items-center justify-between mb-2 lg:mb-4">
            <span className="text-xs lg:text-sm font-medium text-slate-500">CA MAAG</span>
            <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600" />
            </div>
          </div>
          <p className="text-2xl lg:text-3xl font-bold text-slate-900" data-testid="ca-maag">
            {formatMoney(dashboard?.ca_maag_total)}
          </p>
          <p className="text-xs text-slate-400 mt-0.5">En cours + Terminé fact. + Clôturés</p>
          {/* Marge sub-info */}
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BarChart3 className={`w-3.5 h-3.5 ${getMargeColor(dashboard?.marge_globale_pourcentage)}`} />
              <span className="text-xs text-slate-500">Marge globale</span>
            </div>
            <div className="text-right">
              <span className={`text-sm font-bold ${getMargeColor(dashboard?.marge_globale_pourcentage)}`} data-testid="marge-globale">
                {dashboard?.marge_globale_pourcentage ? `${dashboard.marge_globale_pourcentage.toFixed(1)}%` : '-'}
              </span>
              <span className={`ml-2 text-xs ${getMargeColor(dashboard?.marge_globale_pourcentage)}`} data-testid="marge-globale-euros">
                {formatMoney(dashboard?.marge_globale_euros)}
              </span>
            </div>
          </div>
        </div>

        {/* CA MAAG Prévisionnel */}
        <div className="bg-white rounded-xl border border-amber-200 p-4 lg:p-6 shadow-sm" data-testid="kpi-ca-previ-card">
          <div className="flex items-center justify-between mb-2 lg:mb-4">
            <span className="text-xs lg:text-sm font-medium text-amber-600">CA Prévisionnel</span>
            <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 lg:w-5 lg:h-5 text-amber-600" />
            </div>
          </div>
          <p className="text-2xl lg:text-3xl font-bold text-amber-600" data-testid="ca-previsionnel">
            {formatMoney(dashboard?.ca_maag_previsionnel)}
          </p>
          <p className="text-xs text-slate-400 mt-0.5">Prépa + En cours + Terminé fact. + Clôturés</p>
          {/* Marge prévisionnelle sub-info — same layout as CA MAAG */}
          <div className="mt-3 pt-3 border-t border-amber-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BarChart3 className={`w-3.5 h-3.5 ${getMargeColor(dashboard?.marge_previsionnel_pourcentage)}`} />
              <span className="text-xs text-slate-500">Marge prévisionnelle</span>
            </div>
            <div className="text-right">
              <span className={`text-sm font-bold ${getMargeColor(dashboard?.marge_previsionnel_pourcentage)}`} data-testid="marge-previsionnelle">
                {dashboard?.marge_previsionnel_pourcentage !== null && dashboard?.marge_previsionnel_pourcentage !== undefined
                  ? `${dashboard.marge_previsionnel_pourcentage.toFixed(1)}%`
                  : '-'}
              </span>
              <span className={`ml-2 text-xs ${getMargeColor(dashboard?.marge_previsionnel_pourcentage)}`} data-testid="marge-previsionnelle-euros">
                {formatMoney(dashboard?.marge_previsionnel_euros)}
              </span>
            </div>
          </div>
        </div>

        {/* Projets card removed - counters moved next to tabs below */}
        {/* Widget compact Objectifs Annuels (top row, remplace la carte Projets) */}
        {kpi && (kpi.ca_obj_annuel > 0 || kpi.benef_obj_annuel > 0 || kpi.tresorerie_alerte) ? (
          <Link
            to="/admin/suivi-bancaire"
            className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-4 lg:p-6 shadow-sm hover:shadow-lg transition-shadow block sm:col-span-2 lg:col-span-1"
            data-testid="widget-objectifs-annuels"
          >
            <div className="flex items-center justify-between mb-2 lg:mb-3">
              <span className="text-xs lg:text-sm font-medium text-slate-300 flex items-center gap-1.5">
                <Target className="w-3.5 h-3.5 text-emerald-400" />
                Objectifs annuels
              </span>
              <span className="text-[10px] text-slate-500 hover:text-white">Suivi →</span>
            </div>
            <div className="space-y-2.5">
              {kpi.ca_obj_annuel > 0 && (
                <div>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">CA</span>
                    <span className="text-xs text-slate-300">
                      <span className="font-bold text-white">{formatMoney(kpi.ca_realise)}</span>
                      <span className="text-slate-500"> / {formatMoney(kpi.ca_obj_annuel)}</span>
                    </span>
                  </div>
                  <div className="mt-1 h-1 bg-slate-700 rounded-full overflow-hidden relative">
                    {kpi.days_total > 0 && (
                      <div className="absolute top-0 h-full w-0.5 bg-slate-400 z-10" style={{ left: `${Math.min(100, (kpi.days_elapsed / kpi.days_total) * 100)}%` }} />
                    )}
                    <div className={`h-full rounded-full ${(kpi.ca_realise / kpi.ca_obj_annuel) * 100 >= 100 ? 'bg-green-400' : 'bg-blue-400'}`} style={{ width: `${Math.min(100, (kpi.ca_realise / kpi.ca_obj_annuel) * 100)}%` }} />
                  </div>
                </div>
              )}
              {kpi.benef_obj_annuel > 0 && (
                <div>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Bénéfice</span>
                    <span className="text-xs text-slate-300">
                      <span className={`font-bold ${kpi.benef_realise >= 0 ? 'text-white' : 'text-red-400'}`}>{formatMoney(kpi.benef_realise)}</span>
                      <span className="text-slate-500"> / {formatMoney(kpi.benef_obj_annuel)}</span>
                    </span>
                  </div>
                  <div className="mt-1 h-1 bg-slate-700 rounded-full overflow-hidden relative">
                    {kpi.days_total > 0 && (
                      <div className="absolute top-0 h-full w-0.5 bg-slate-400 z-10" style={{ left: `${Math.min(100, (kpi.days_elapsed / kpi.days_total) * 100)}%` }} />
                    )}
                    <div className={`h-full rounded-full ${(kpi.benef_realise / kpi.benef_obj_annuel) * 100 >= 100 ? 'bg-green-400' : 'bg-blue-400'}`} style={{ width: `${Math.max(0, Math.min(100, (kpi.benef_realise / kpi.benef_obj_annuel) * 100))}%` }} />
                  </div>
                </div>
              )}
              <div className={`flex items-baseline justify-between gap-2 ${kpi.tresorerie_alerte ? 'pt-1 border-t border-red-700/50' : 'pt-1 border-t border-slate-700'}`}>
                <span className={`text-[10px] uppercase tracking-wide font-semibold flex items-center gap-1 ${kpi.tresorerie_alerte ? 'text-red-400' : 'text-slate-400'}`}>
                  {kpi.tresorerie_alerte && <AlertTriangle className="w-2.5 h-2.5" />}
                  Trésorerie
                </span>
                <span className={`text-xs font-bold ${kpi.tresorerie_alerte ? 'text-red-300' : 'text-white'}`}>{formatMoney(kpi.tresorerie)}</span>
              </div>
            </div>
          </Link>
        ) : (
          <Link
            to="/admin/suivi-bancaire"
            className="bg-white rounded-xl border border-dashed border-slate-300 p-4 lg:p-6 flex flex-col items-center justify-center text-center hover:border-slate-400 hover:bg-slate-50 transition sm:col-span-2 lg:col-span-1"
            data-testid="widget-objectifs-vide"
          >
            <Target className="w-6 h-6 text-slate-400 mb-2" />
            <p className="text-xs lg:text-sm font-medium text-slate-600">Configure tes objectifs annuels</p>
            <p className="text-[11px] text-slate-500 mt-1">CA, bénéfice, trésorerie · alertes</p>
          </Link>
        )}
      </div>

      {/* Ligne unique « Alertes société » : relevés manquants, chantiers < 15 %, devis signés (7 j), factures à payer */}
      {(() => {
        const nonRentables = dashboard?.projets_non_rentables || [];
        const signes7j = (dashboard?.devis_signes_recents || []).filter((d) => Date.now() - new Date(d.signed_at).getTime() < 7 * 86400000);
        const nbFactures = dashboard?.factures_a_payer_count || 0;
        const nbUrgentes = dashboard?.factures_urgentes_count || 0;
        const nbReleves = moisManquantsAlerte.length;
        const total = (nbReleves ? 1 : 0) + (nonRentables.length ? 1 : 0) + (signes7j.length ? 1 : 0) + (nbFactures ? 1 : 0);
        if (total === 0) return null;
        const chip = 'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-xs font-medium border transition-colors';
        const fmtDate = (iso) => new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
        const moisLabels = ['Janv.', 'Févr.', 'Mars', 'Avril', 'Mai', 'Juin', 'Juil.', 'Août', 'Sept.', 'Oct.', 'Nov.', 'Déc.'];
        return (
          <div className="mb-4 lg:mb-6 rounded-lg border border-slate-200 bg-white" data-testid="alertes-societe">
            <div className="flex flex-wrap items-center gap-2 px-3 py-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 uppercase tracking-wide mr-1">
                <AlertTriangle className="w-3.5 h-3.5 text-slate-400" /> Alertes société
              </span>
              {nbReleves > 0 && (
                <Link to="/admin/suivi-bancaire" className={`${chip} border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100`} title={`Mois : ${moisManquantsAlerte.map((m) => moisLabels[m - 1]).join(', ')}`} data-testid="dashboard-alerte-releves-manquants">
                  {nbReleves} relevé{nbReleves > 1 ? 's' : ''} bancaire{nbReleves > 1 ? 's' : ''} manquant{nbReleves > 1 ? 's' : ''}
                </Link>
              )}
              {nonRentables.length > 0 && (
                <button type="button" onClick={() => { setShowMargeDetail((v) => !v); setShowSignesDetail(false); }} className={`${chip} border-red-200 bg-red-50 text-red-700 hover:bg-red-100 ${showMargeDetail ? 'ring-2 ring-red-300' : ''}`} data-testid="alerte-marge-toggle">
                  {nonRentables.length} chantier{nonRentables.length > 1 ? 's' : ''} sous 15 % de marge <ChevronDown className={`w-3 h-3 transition-transform ${showMargeDetail ? 'rotate-180' : ''}`} />
                </button>
              )}
              {signes7j.length > 0 && (
                <button type="button" onClick={() => { setShowSignesDetail((v) => !v); setShowMargeDetail(false); }} className={`${chip} border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 ${showSignesDetail ? 'ring-2 ring-emerald-300' : ''}`} data-testid="devis-signes-toggle">
                  <FileSignature className="w-3 h-3" /> {signes7j.length} devis signé{signes7j.length > 1 ? 's' : ''} (7 j) <ChevronDown className={`w-3 h-3 transition-transform ${showSignesDetail ? 'rotate-180' : ''}`} />
                </button>
              )}
              {nbFactures > 0 && (
                <Link to="/fournisseurs" className={`${chip} ${nbUrgentes > 0 ? 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100' : 'border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100'}`} data-testid="alerte-factures-chip">
                  <FileText className="w-3 h-3" /> {nbFactures} facture{nbFactures > 1 ? 's' : ''} à payer · {formatMoney(dashboard?.factures_a_payer_total)}{nbUrgentes > 0 ? ` · ${nbUrgentes} urgente${nbUrgentes > 1 ? 's' : ''}` : ''}
                </Link>
              )}
            </div>
            {showMargeDetail && nonRentables.length > 0 && (
              <div className="border-t border-slate-100 px-3 py-1.5 divide-y divide-slate-100" data-testid="alerte-marge-detail">
                {[...nonRentables].sort((a, b) => (a.marge_pourcentage ?? 0) - (b.marge_pourcentage ?? 0)).map((p) => (
                  <Link key={p.id} to={`/projets/${p.id}`} className="flex items-center justify-between py-1.5 text-xs hover:bg-slate-50 px-1 rounded">
                    <span className="font-medium text-slate-800 truncate">{p.nom}</span>
                    <span className="flex items-center gap-3 shrink-0"><span className="text-slate-400">{formatMoney(p.marge_euros)}</span><span className="text-red-600 font-semibold w-12 text-right">{p.marge_pourcentage?.toFixed(1)}%</span></span>
                  </Link>
                ))}
              </div>
            )}
            {showSignesDetail && signes7j.length > 0 && (
              <div className="border-t border-slate-100 px-3 py-1.5 divide-y divide-slate-100" data-testid="devis-signes-detail">
                {signes7j.map((d) => (
                  <Link key={d.devis_id} to={`/devis/${d.devis_id}`} className="flex items-center justify-between gap-3 py-1.5 text-xs hover:bg-slate-50 px-1 rounded">
                    <span className="min-w-0 truncate"><span className="font-medium text-slate-800">{d.projet_nom}</span><span className="text-slate-500"> — {d.objet}{d.version ? ` (v${d.version})` : ''}</span>{d.signataire_nom && <span className="text-slate-400"> · {d.signataire_nom}</span>}</span>
                    <span className="flex items-center gap-3 shrink-0"><span className="text-slate-400">{fmtDate(d.signed_at)}</span><span className="text-emerald-700 font-semibold">{formatMoney(d.total_ttc)}</span></span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {newProjetOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setNewProjetOpen(false)} data-testid="dash-new-projet-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-slate-900 mb-1">Nouveau projet</h3>
            <p className="text-sm text-slate-500 mb-4">Choisissez l'enseigne déjà enregistrée pour ce chantier.</p>
            <select
              value={newProjetEnseigne}
              onChange={(e) => setNewProjetEnseigne(e.target.value)}
              className="w-full h-11 px-3 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 mb-4"
              data-testid="dash-new-projet-enseigne"
              autoFocus
            >
              <option value="">{enseignesList.length ? '— Sélectionner une enseigne —' : 'Chargement…'}</option>
              {enseignesList.map((e) => (
                <option key={e.id} value={e.id}>{e.nom}</option>
              ))}
            </select>
            <div className="flex justify-between items-center gap-2">
              <Link to="/clients" className="text-xs text-slate-500 hover:text-slate-800 underline" onClick={() => setNewProjetOpen(false)}>Créer une nouvelle enseigne</Link>
              <div className="flex gap-2">
                <button onClick={() => setNewProjetOpen(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Annuler</button>
                <button onClick={confirmNewProjet} className="px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700" data-testid="dash-new-projet-confirm">Continuer →</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Liste des projets */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-slate-200">
          <div className="flex items-center justify-between mb-3 lg:mb-4">
            <h2 className="text-base lg:text-lg font-semibold text-slate-900">Projets</h2>
            <Link
              to="/clients"
              className="text-xs lg:text-sm text-blue-600 hover:text-blue-800"
            >
              Enseignes →
            </Link>
          </div>
          
          {/* Tabs + compteurs statuts */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('en_cours')}
                className={`flex items-center gap-1 lg:gap-2 px-3 lg:px-4 py-2 rounded-lg text-xs lg:text-sm font-medium transition-colors ${
                  activeTab === 'en_cours'
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Play className="w-3 h-3 lg:w-4 lg:h-4" />
                <span className="hidden sm:inline">En cours</span> ({projetsActifs.length})
              </button>
              <button
                onClick={() => setActiveTab('archives')}
                className={`flex items-center gap-1 lg:gap-2 px-3 lg:px-4 py-2 rounded-lg text-xs lg:text-sm font-medium transition-colors ${
                  activeTab === 'archives'
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Archive className="w-3 h-3 lg:w-4 lg:h-4" />
                <span className="hidden sm:inline">Archives</span> ({projetsArchives.length})
              </button>
              <button
                onClick={openNewProjet}
                className="flex items-center gap-1 lg:gap-2 px-3 lg:px-4 py-2 rounded-lg text-xs lg:text-sm font-semibold bg-red-600 text-white hover:bg-red-700 shadow-sm transition-colors"
                title="Créer un nouveau projet pour une enseigne existante"
                data-testid="dash-new-projet-btn"
              >
                <Plus className="w-3 h-3 lg:w-4 lg:h-4" />
                <span className="hidden sm:inline">Nouveau projet</span>
              </button>
            </div>
            <button
              onClick={toggleGroupByEnseigne}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs lg:text-sm font-medium border transition-colors ${groupByEnseigne ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
              title={groupByEnseigne ? 'Revenir à la liste (ordre personnalisé, glisser-déposer)' : 'Regrouper les dossiers par enseigne'}
              data-testid="toggle-group-enseigne"
            >
              <Building2 className="w-4 h-4" />
              <span className="hidden sm:inline">Par enseigne</span>
            </button>
            {/* Compteurs par statut (déplacés de la carte KPI supprimée) */}
            <div className="flex items-center gap-3 lg:gap-4 text-xs lg:text-sm">
              <span className="text-slate-500" data-testid="count-prep"><span className="font-bold text-blue-600">{dashboard?.projets_en_preparation || 0}</span> Prépa</span>
              <span className="text-slate-500" data-testid="count-encours"><span className="font-bold text-green-600">{dashboard?.projets_en_cours || 0}</span> En cours</span>
              <span className="text-slate-500" data-testid="count-factu"><span className="font-bold text-purple-600">{dashboard?.projets_termine_facturation || 0}</span> Factu.</span>
              <span className="text-slate-500" data-testid="count-standby"><span className="font-bold text-amber-600">{dashboard?.projets_stand_by || 0}</span> Stand-by</span>
              <span className="text-slate-500" data-testid="count-clot"><span className="font-bold text-slate-600">{dashboard?.projets_clotures || 0}</span> Clôturés</span>
            </div>
          </div>
        </div>

        {/* Tab: En cours */}
        {activeTab === 'en_cours' && (
          <>
            {/* --- Section 1 : Prioritaires (En cours + Préparation) - toujours visibles --- */}
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto" data-testid="section-prioritaires">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <table className="w-full">
                  {renderTableHead()}
                  <SortableContext items={projetsPrioritaires.map(p => p.id)} strategy={verticalListSortingStrategy}>
                    <tbody className="divide-y divide-slate-100">
                      {projetsPrioritaires.length === 0 ? (
                        <tr>
                          <td colSpan="12" className="px-6 py-12 text-center text-slate-500">
                            Aucun projet en cours ou en préparation. <Link to="/clients" className="text-blue-600 hover:underline">Créer depuis une enseigne</Link>
                          </td>
                        </tr>
                      ) : groupByEnseigne ? renderGroupedRows(projetsPrioritaires) : (
                        projetsPrioritaires.map((projet) => (
                          <SortableRow key={projet.id} projet={projet}>
                            {renderRowCells(projet)}
                          </SortableRow>
                        ))
                      )}
                    </tbody>
                  </SortableContext>
                </table>
              </DndContext>
            </div>

            {/* Mobile Cards - Prioritaires */}
            <div className="lg:hidden divide-y divide-slate-100">
              {projetsPrioritaires.length === 0 ? (
                <div className="px-4 py-8 text-center text-slate-500 text-sm">
                  Aucun projet en cours ou en préparation. <Link to="/clients" className="text-blue-600 hover:underline">Créer depuis une enseigne</Link>
                </div>
              ) : groupByEnseigne ? renderGroupedMobile(projetsPrioritaires) : (
                projetsPrioritaires.map(renderMobileCard)
              )}
            </div>

            {/* --- Section 2 : Stand-by (repliable) --- */}
            {projetsStandBy.length > 0 && (
              <div className="border-t border-slate-200" data-testid="section-stand-by">
                <button
                  onClick={() => setShowStandBy(v => !v)}
                  className="w-full flex items-center justify-between px-4 lg:px-6 py-3 bg-amber-50 hover:bg-amber-100 transition-colors"
                  data-testid="toggle-stand-by"
                >
                  <div className="flex items-center gap-2">
                    {showStandBy ? <ChevronDown className="w-4 h-4 text-amber-700" /> : <ChevronRight className="w-4 h-4 text-amber-700" />}
                    <Pause className="w-4 h-4 text-amber-700" />
                    <span className="font-semibold text-sm text-amber-900">Projets en stand-by</span>
                    <span className="text-xs text-amber-700 bg-amber-200 px-2 py-0.5 rounded-full">{projetsStandBy.length}</span>
                  </div>
                </button>
                {showStandBy && (
                  <>
                    {/* Desktop */}
                    <div className="hidden lg:block overflow-x-auto">
                      <table className="w-full">
                        {renderTableHead()}
                        <tbody className="divide-y divide-slate-100">
                          {groupByEnseigne ? renderGroupedRows(projetsStandBy) : projetsStandBy.map((projet) => (
                            <StaticRow key={projet.id} projet={projet}>
                              {renderRowCells(projet)}
                            </StaticRow>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Mobile */}
                    <div className="lg:hidden divide-y divide-slate-100">
                      {groupByEnseigne ? renderGroupedMobile(projetsStandBy) : projetsStandBy.map(renderMobileCard)}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* --- Section 3 : Terminé facturation (repliable) --- */}
            {projetsTermines.length > 0 && (
              <div className="border-t border-slate-200" data-testid="section-termines">
                <button
                  onClick={() => setShowTermines(v => !v)}
                  className="w-full flex items-center justify-between px-4 lg:px-6 py-3 bg-purple-50 hover:bg-purple-100 transition-colors"
                  data-testid="toggle-termines"
                >
                  <div className="flex items-center gap-2">
                    {showTermines ? <ChevronDown className="w-4 h-4 text-purple-700" /> : <ChevronRight className="w-4 h-4 text-purple-700" />}
                    <FileText className="w-4 h-4 text-purple-700" />
                    <span className="font-semibold text-sm text-purple-900">Projets terminés – facturation</span>
                    <span className="text-xs text-purple-700 bg-purple-200 px-2 py-0.5 rounded-full">{projetsTermines.length}</span>
                  </div>
                </button>
                {showTermines && (
                  <>
                    {/* Desktop */}
                    <div className="hidden lg:block overflow-x-auto">
                      <table className="w-full">
                        {renderTableHead()}
                        <tbody className="divide-y divide-slate-100">
                          {groupByEnseigne ? renderGroupedRows(projetsTermines) : projetsTermines.map((projet) => (
                            <StaticRow key={projet.id} projet={projet}>
                              {renderRowCells(projet)}
                            </StaticRow>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Mobile */}
                    <div className="lg:hidden divide-y divide-slate-100">
                      {groupByEnseigne ? renderGroupedMobile(projetsTermines) : projetsTermines.map(renderMobileCard)}
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* Tab: Archives */}
        {activeTab === 'archives' && (
          <div className="p-6">
            {projetsArchives.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Archive className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>Aucun projet archivé</p>
                <p className="text-sm mt-2">Les projets clôturés apparaîtront ici</p>
              </div>
            ) : (
              <div className="space-y-4">
                {anneesTriees.map((annee) => {
                  const enseignesParAnnee = archivesParAnnee[annee];
                  const enseignesTriees = Object.keys(enseignesParAnnee).sort();
                  const isExpanded = expandedYears[annee] !== false; // Expanded by default
                  
                  // Calculer les totaux de l'année
                  const totalCA = Object.values(enseignesParAnnee).flat().reduce((sum, p) => sum + (p.ca_maag || 0), 0);
                  const totalMarge = Object.values(enseignesParAnnee).flat().reduce((sum, p) => sum + (p.marge_euros || 0), 0);
                  const nbProjets = Object.values(enseignesParAnnee).flat().length;
                  
                  return (
                    <div key={annee} className="border border-slate-200 rounded-xl overflow-hidden">
                      {/* Year Header */}
                      <button
                        onClick={() => toggleYear(annee)}
                        className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-slate-100 to-slate-50 hover:from-slate-200 hover:to-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center">
                            <Calendar className="w-5 h-5 text-white" />
                          </div>
                          <div className="text-left">
                            <h3 className="text-lg font-bold text-slate-900">Archives {annee}</h3>
                            <p className="text-sm text-slate-500">
                              {nbProjets} projet(s) • {enseignesTriees.length} enseigne(s)
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className="text-xs text-slate-500">CA Total</p>
                            <p className="font-mono font-semibold text-slate-900">{formatMoney(totalCA)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Marge</p>
                            <p className={`font-mono font-bold ${totalMarge >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatMoney(totalMarge)}
                            </p>
                          </div>
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          )}
                        </div>
                      </button>
                      
                      {/* Enseignes List */}
                      {isExpanded && (
                        <div className="divide-y divide-slate-100">
                          {enseignesTriees.map((enseigne) => {
                            const projetsEnseigne = enseignesParAnnee[enseigne];
                            const enseigneCA = projetsEnseigne.reduce((sum, p) => sum + (p.ca_maag || 0), 0);
                            const enseigneMarge = projetsEnseigne.reduce((sum, p) => sum + (p.marge_euros || 0), 0);
                            
                            return (
                              <div key={enseigne} className="p-4 bg-white">
                                <div className="flex items-center gap-3 mb-3">
                                  <Building2 className="w-5 h-5 text-indigo-500" />
                                  <h4 className="font-semibold text-slate-900">{enseigne}</h4>
                                  <span className="text-sm text-slate-500">
                                    ({projetsEnseigne.length} projet{projetsEnseigne.length > 1 ? 's' : ''})
                                  </span>
                                  <div className="ml-auto flex items-center gap-4 text-sm">
                                    <span className="text-slate-600">CA: <span className="font-mono font-medium">{formatMoney(enseigneCA)}</span></span>
                                    <span className={enseigneMarge >= 0 ? 'text-green-600' : 'text-red-600'}>
                                      Marge: <span className="font-mono font-bold">{formatMoney(enseigneMarge)}</span>
                                    </span>
                                  </div>
                                </div>
                                <div className="grid gap-2 pl-8">
                                  {projetsEnseigne.map((projet) => (
                                    <Link
                                      key={projet.id}
                                      to={`/projets/${projet.id}`}
                                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                                    >
                                      <div className="flex items-center gap-3">
                                        <FolderOpen className="w-4 h-4 text-slate-400" />
                                        <span className="font-medium text-slate-900">{projet.ville}</span>
                                        {projet.adresse && (
                                          <span className="text-sm text-slate-500">• {projet.adresse}</span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-4 text-sm">
                                        <span className="text-slate-600 font-mono">{formatMoney(projet.ca_maag)}</span>
                                        <span className={`font-mono font-semibold ${projet.marge_pourcentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                          {projet.marge_pourcentage !== null ? `${projet.marge_pourcentage.toFixed(1)}%` : '-'}
                                        </span>
                                        <button
                                          onClick={(e) => {
                                            e.preventDefault();
                                            handleDeleteProjet(projet.id, projet.nom_complet);
                                          }}
                                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                          title="Supprimer le projet"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
